/**
 * BrandProfileContext — caches the brand profile so every page can access
 * primary_vertical, secondary_verticals, brand_name without re-fetching.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getBrandProfile, updateBrandProfile } from '../services/brand';
import { useAuth } from './AuthContext';

const BrandProfileContext = createContext(null);

export function BrandProfileProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getBrandProfile();
      setProfile(data);
      setError(null);
      return data;
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.warn('Profile fetch failed:', err.message);
      setError(err.response?.data?.detail || err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) refresh();
  }, [isAuthenticated, refresh]);

  const save = useCallback(async (payload) => {
    const updated = await updateBrandProfile(payload);
    setProfile(updated);
    return updated;
  }, []);

  const value = useMemo(() => ({
    profile,
    loading,
    error,
    refresh,
    save,
    primaryVertical: profile?.primary_vertical || null,
    secondaryVerticals: profile?.secondary_verticals || [],
    competitors: profile?.competitors || [],
    skus: profile?.skus || [],
    brandName: profile?.brand_name || 'Brand',
  }), [profile, loading, error, refresh, save]);

  return <BrandProfileContext.Provider value={value}>{children}</BrandProfileContext.Provider>;
}

export function useBrandProfile() {
  const ctx = useContext(BrandProfileContext);
  if (!ctx) throw new Error('useBrandProfile must be used within BrandProfileProvider');
  return ctx;
}
